/* eslint-disable react/jsx-handler-names */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Chip from "../../atoms/chip";
import CustomImage from "../../atoms/customImage";
import Typography from "../../atoms/typography";
import HTMLReactParser from "html-react-parser";
import { isJsonString } from "../../../utils/common";
import { useDispatch } from "react-redux";
import { setIsNavigatingAction } from "../../../store/slices/commonSlice";
import useAnalytics from "../../../analytics/useAnalytics";
import { homePageConstants } from "../../../constants/pageConstants/homeConstants";
import { campaignEvents } from "../../../analytics/clevertapConstants";

/**
 * QuickLinks component renders a list of quick links.
 * @param {Object[]} isMobile - Boolean to determine the mobile device.
 * @param {Object[]} dataList - Array of objects containing type, imageURL, categoryName, categoryId, redirectionUrl,  .
 * @param {Object[]} fallbackHTML - render HTML for each quick link.
 * @returns {JSX.Element} QuickLinks component.
 */
const QuickLinks = ({ isMobile, dataList, fallbackHTML }) => {
  const dispatch = useDispatch();
  const chipType = dataList?.capsuleType;
  const itemsList = dataList?.data?.length ? dataList?.data : fallbackHTML;

  const [displayItems, setDisplayItems] = useState(dataList?.data || []);
  const [campaignEventData, setCampaignEventData] = useState({});

  const staticCMSItems = useRef(dataList?.data || []);
  const campaignIconList = useRef([]);
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (dataList?.data && dataList.data.length > 0) {
      staticCMSItems.current = dataList.data;
      campaignIconList.current = [];
      setCampaignEventData({});
      setDisplayItems(dataList.data);
    }
  }, [dataList]);

  useEffect(() => {
    /**
     * Handles the "CT_web_native_display" event and sets the appropriate state
     * based on the delivery topic.
     *
     * @param {object} event .
     */
    const handleEvent = (event) => {
      const { kv } = event.detail;
      if (kv.topic !== homePageConstants.ALPHA_NATIVE_ICONS) return;

      trackEvent(campaignEvents.RENDER_NOTIFICATION_VIEWED, event?.detail, ["clevertap"]);
      setCampaignEventData((prev) => ({ ...prev, [event.detail.msgId]: event?.detail }));

      const { desktopAlphaIcon, mobileAlphaIcon } = kv;
      const desktopIconsJSON = isJsonString(desktopAlphaIcon) ? JSON.parse(desktopAlphaIcon) : [];
      const mobileIconsJSON = isJsonString(mobileAlphaIcon) ? JSON.parse(mobileAlphaIcon) : [];
      const deviceSpecificJson = isMobile ? mobileIconsJSON : desktopIconsJSON;

      const incomingCampaignIds = deviceSpecificJson.map((item) => item.campaign_id);
      const filteredCampaignItems = campaignIconList.current.filter(
        (item) => !incomingCampaignIds.includes(item.campaign_id),
      );
      campaignIconList.current = [...filteredCampaignItems, ...deviceSpecificJson].sort(
        (a, b) => a.position - b.position,
      );

      let newList = [...staticCMSItems.current];
      const sortedCampaigns = [...campaignIconList.current].sort((a, b) => a.position - b.position);

      sortedCampaigns.forEach((item) => {
        const position = item.position - 1;
        const mergedItem = {
          imageUrl: item.imageUrl,
          redirectionUrl: item.redirectionUrl,
          categoryName: item.categoryName,
          categoryId: item.categoryId,
          campaignId: item.campaignId,
          alt: item.alt || item.categoryName,
          backgroundColor: item.backgroundDetail,
        };

        newList.splice(position, 0, mergedItem);
      });

      setDisplayItems(newList);
    };

    document.addEventListener("CT_web_native_display", handleEvent);
    return () => {
      document.removeEventListener("CT_web_native_display", handleEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignEventData, isMobile]);

  /**
   * When capsule data is not available, these fallback chips will be visible.
   * @returns
   */
  const getFallbackChips = () =>
    !isJsonString(fallbackHTML)
      ? HTMLReactParser(fallbackHTML)
      : JSON.parse(itemsList)?.map((item, index) => (
          <a
            href={item?.contentLink || "#"}
            key={index}
            className="cursor-pointer first:ps-16 last:pr-16"
            onClick={() => dispatch(setIsNavigatingAction(true))}
          >
            <Chip id={`chip-${index}`} content={item?.textContent} variant="outlined" size="medium" classes="text-sm" />
          </a>
        ));
  /**
   *
   */
  const getChips = () =>
    displayItems?.map((item, index) =>
      chipType === "text" ? (
        <a
          href={item?.redirectionUrl || "#"}
          key={item?.categoryId}
          className="cursor-pointer first:ps-16 last:pr-16"
          onClick={() => dispatch(setIsNavigatingAction(true))}
        >
          <Chip id={`chip-${index}`} content={item?.categoryName} variant="outlined" size="medium" classes="text-sm" />
        </a>
      ) : (
        <a
          href={item?.redirectionUrl}
          key={item?.categoryId}
          className=" cursor-pointer first:ps-16 last:pr-16"
          onClick={() => dispatch(setIsNavigatingAction(true))}
        >
          <CustomImage
            src={item?.imageUrl || "#"}
            alt={item?.alt || item?.categoryName}
            width={isMobile ? 76 : 133}
            height={isMobile ? 76 : 133}
            className={`mx-auto rounded-2xl ${isMobile ? "max-w-[100px]" : "h-[133px] max-w-[133px]"}`}
          />
          <Typography Tag="p" classes="text-12 font-500 leading-14 text-fnp-500 mt-4 overflow-hidden text-ellipsis">
            {item?.categoryName?.length > 15 && isMobile
              ? `${item?.categoryName?.slice(0, 15)}...`
              : item?.categoryName}
          </Typography>
        </a>
      ),
    );

  return (
    <div
      className={`${!isJsonString(fallbackHTML) && !dataList?.data?.length ? "block" : "-mx-16 mb-16 flex"} gap-12 overflow-x-auto text-center scrollbar-none ${isMobile ? `mb-18 ${!isJsonString(fallbackHTML) ? "px-4" : ""}` : !isJsonString(fallbackHTML) ? "" : "mb-40 mt-48"}`}
    >
      {chipType ? getChips() : getFallbackChips()}
    </div>
  );
};

QuickLinks.propTypes = {
  isMobile: PropTypes.bool,
  dataList: PropTypes.object,
  fallbackHTML: PropTypes.any,
};

export default QuickLinks;
